"use client";

import { useEffect, useRef } from "react";
import { Navigation, Pagination, A11y } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";
import type { Swiper as SwiperType } from "swiper";
import "swiper/css";
import "swiper/css/pagination";

import { BeerCard } from "@/components/cards/beer-card";
import { Icon } from "@/components/icon/icon";
import type { BeerOnTap } from "@/lib/real-ale-finder/real-ale-finder.types";
import messages from "@/messages/en.json";

interface NewOnTapSwiperProps {
  beers: BeerOnTap[];
}

export function NewOnTapSwiper({ beers }: NewOnTapSwiperProps) {
  const paginationRef = useRef<HTMLDivElement>(null);
  const swiperRef = useRef<SwiperType | null>(null);

  useEffect(() => {
    const swiper = swiperRef.current;
    if (!swiper || !paginationRef.current) return;

    if (swiper.params.pagination && typeof swiper.params.pagination !== "boolean") {
      swiper.params.pagination.el = paginationRef.current;
    }
    swiper.pagination.init();
    swiper.pagination.render();
    swiper.pagination.update();
  }, []);

  return (
    <div>
      <div className="-mx-page-x-mobile">
        <Swiper
          modules={[Navigation, Pagination, A11y]}
          slidesPerView="auto"
          spaceBetween={8}
          navigation={{ prevEl: ".new-on-tap-prev", nextEl: ".new-on-tap-next" }}
          pagination={{ clickable: true, enabled: false }}
          a11y={{
            containerMessage: messages.whatsOnTap.homeSection.carousel.containerLabel,
            containerRoleDescriptionMessage: messages.whatsOnTap.homeSection.carousel.containerRoleDescription,
            prevSlideMessage: messages.whatsOnTap.homeSection.carousel.prevSlide,
            nextSlideMessage: messages.whatsOnTap.homeSection.carousel.nextSlide,
          }}
          onSwiper={(swiper: SwiperType) => { swiperRef.current = swiper; }}
          style={{ paddingInline: "0.9375rem" }}
          className="w-full overflow-visible!"
        >
          {beers.map((beer) => (
            <SwiperSlide key={beer.id} style={{ width: "318px" }}>
              {/* Plain BeerCard inside the swiper — the flip would
                  fight swipe gestures and the rotation isn't useful in a
                  carousel context. Desktop grid still uses the
                  interactive variant. */}
              <BeerCard beer={beer} compact />
            </SwiperSlide>
          ))}
        </Swiper>
      </div>

      {/* Controls row: arrows left, dots right */}
      <div className="mt-6 flex items-center justify-between">
        <div className="flex items-center gap-6">
          <button
            aria-label={messages.whatsOnTap.homeSection.carousel.prevSlide}
            className="new-on-tap-prev text-hop disabled:opacity-30 cursor-pointer"
          >
            <Icon name="arrow" className="w-2.25 h-4" />
          </button>
          <button
            aria-label={messages.whatsOnTap.homeSection.carousel.nextSlide}
            className="new-on-tap-next text-hop disabled:opacity-30 cursor-pointer"
          >
            <Icon name="arrow" className="w-2.25 h-4 rotate-180" />
          </button>
        </div>
        <div ref={paginationRef} className="new-on-tap-pagination flex items-center gap-1 ml-auto justify-end" />
      </div>
    </div>
  );
}
